import {useEffect, useRef} from 'react'
import {useNavigate, useSearchParams} from 'react-router-dom'
import {useLazyGetProgramSignalThresholdViolationByIdQuery} from '@apis/lineSignals'
import {
  buildInvestigateViolationPrompt,
  investigateViolationPageContext,
} from '@shared/alpinist/investigateViolationPrompt'
import {useAlpinistShortcut} from '@shared/hooks/useAlpinistShortcut'
import {useCustomTranslation} from '@shared/hooks/useCustomTranslation'
import {useSnackbar} from '@shared/hooks/useSnackbar'

/** The query param alert links carry (pm-core ``INVESTIGATE_PARAM``). */
export const INVESTIGATE_PARAM = 'investigate'

/**
 * useInvestigateFromUrl - opens Alpinist on a threshold violation when the app
 * is reached through an alert's "Investigate with Alpinist" link (AB#6581):
 * `<any page>?investigate=<violation id>`.
 *
 * The link carries only the id. The violation is fetched and the question is
 * built here, so a link cannot smuggle prompt text into the user's
 * conversation. The param is dropped from the URL first, so reloading or going
 * back does not start a second conversation. The login redirect keeps the
 * whole original URL (index.tsx `originalUrl`), so a signed-out click still
 * lands here after sign-in.
 */
export const useInvestigateFromUrl = (): void => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const violationId = searchParams.get(INVESTIGATE_PARAM)
  const [fetchViolation] = useLazyGetProgramSignalThresholdViolationByIdQuery()
  const {askAlpinist} = useAlpinistShortcut()
  const [showNotification] = useSnackbar()
  const [t] = useCustomTranslation('alpinist')
  const handledRef = useRef<string | null>(null)

  useEffect(() => {
    if (!violationId || handledRef.current === violationId) {
      return
    }
    handledRef.current = violationId

    // Read the URL as it is NOW, not as this render saw it: the page underneath
    // (the Signal Explorer syncs its own params) may already have rewritten it.
    const current = new URLSearchParams(window.location.search)
    if (current.has(INVESTIGATE_PARAM)) {
      current.delete(INVESTIGATE_PARAM)
      const search = current.toString()
      navigate(
        {
          pathname: window.location.pathname,
          search: search ? `?${search}` : '',
          hash: window.location.hash,
        },
        {replace: true},
      )
    }

    fetchViolation({violationId})
      .unwrap()
      .then((violation) =>
        askAlpinist({
          conversationTitle: violation.program_signal_threshold.name,
          prompt: buildInvestigateViolationPrompt(violation),
          pageContext: investigateViolationPageContext(violation),
        }),
      )
      .catch(() => {
        showNotification({message: t('investigate_not_found'), severity: 'error'})
      })
  }, [violationId, fetchViolation, askAlpinist, navigate, showNotification, t])
}
